import React, { useState, useCallback } from 'react';
import { Container, Card, Button, Alert } from 'react-bootstrap';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Helmet } from 'react-helmet-async';
import { useDropzone } from 'react-dropzone';
import ProcessingProgress from './common/ProcessingProgress';
import useProcessingProgress from '../hooks/useProcessingProgress';
import '../styles/components.css';

const SheetSplitter = () => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');

  const {
    progress,
    status,
    isComplete,
    isLoading,
    simulateProgress
  } = useProcessingProgress();

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setError('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const splittingStages = [
    { progress: 0, status: 'Starting sheet splitting process...', duration: 500 },
    { progress: 20, status: 'Loading workbook...', duration: 800 },
    { progress: 40, status: 'Analyzing worksheets...', duration: 1000 },
    { progress: 60, status: 'Creating individual workbooks...', duration: 1200 },
    { progress: 80, status: 'Preserving formatting and data...', duration: 1000 },
    { progress: 100, status: 'Complete!', duration: 500 }
  ];

  const splitWorkbook = async () => {
    if (!file) {
      setError('Please select an Excel file');
      return;
    }

    try {
      // Start progress animation
      await simulateProgress(splittingStages);
      
      // Now process the file after progress animation is complete
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(e.target.result);
          const totalSheets = workbook.worksheets.length;

          for (let i = 0; i < totalSheets; i++) {
            const sheet = workbook.worksheets[i];
            const newWorkbook = new ExcelJS.Workbook();
            const newSheet = newWorkbook.addWorksheet(sheet.name);

            // Helper function to extract cell data
            function extractCellData(cell) {
              const data = {
                address: cell.address,
                style: { ...cell.style }
              };
              
              // Handle value and rich text
              if (cell.value && cell.value.richText) {
                data.value = cell.value;
              } else {
                data.value = cell.value;
              }

              return data;
            }

            // First copy column properties
            sheet.columns.forEach((col, index) => {
              if (col.width) {
                newSheet.getColumn(index + 1).width = col.width;
              }
            });

            // Process each row
            sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
              const rowData = {
                number: rowNumber,
                height: row.height,
                cells: []
              };

              // First pass: collect all cell data
              const cellsInRow = [];
              row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                const cellData = extractCellData(cell);
                cellsInRow.push({
                  colNumber,
                  data: cellData,
                  isMerged: cell.isMerged,
                  masterAddress: cell.isMerged ? cell.master.address : null
                });
              });

              // Second pass: handle merged cells
              let lastValue = null;
              let lastColNumber = null;
              
              cellsInRow.forEach((cell) => {
                const currentValue = JSON.stringify(cell.data.value);
                
                if (cell.isMerged) {
                  if (cell.masterAddress === cell.data.address) {
                    lastValue = currentValue;
                    lastColNumber = cell.colNumber;
                    rowData.cells.push(cell.data);
                    
                    // Track merge range for later application
                    const mergeStartCell = sheet.getCell(cell.masterAddress);
                    const mergeEndCell = sheet.getCell(mergeStartCell.model.master.split(':')[1]);
                    newSheet.mergeCells(mergeStartCell.address + ':' + mergeEndCell.address);
                  } else {
                    const clearedData = { ...cell.data, value: null };
                    rowData.cells.push(clearedData);
                  }
                } else {
                  if (currentValue === lastValue && cell.colNumber === lastColNumber + 1) {
                    const clearedData = { ...cell.data, value: null };
                    rowData.cells.push(clearedData);
                  } else {
                    lastValue = currentValue;
                    lastColNumber = cell.colNumber;
                    rowData.cells.push(cell.data);
                  }
                }
              });

              // Apply row and cell data
              const targetRow = newSheet.getRow(rowData.number);
              
              // First apply all cell data and styles
              rowData.cells.forEach(cellData => {
                const targetCell = newSheet.getCell(cellData.address);
                targetCell.value = cellData.value;
                if (Object.keys(cellData.style).length > 0) {
                  targetCell.style = cellData.style;
                }
              });

              // Then apply row height
              if (rowData.height !== undefined) {
                targetRow.height = rowData.height;
              }

              // Finally commit the row
              targetRow.commit();
            });

            // Copy sheet properties and views last
            newSheet.properties = JSON.parse(JSON.stringify(sheet.properties));
            newSheet.views = JSON.parse(JSON.stringify(sheet.views));
            
            // Ensure gridlines are hidden if they were hidden in source
            if (sheet.views && sheet.views[0] && !sheet.views[0].showGridLines) {
              newSheet.views = [{ showGridLines: false }];
            }

            const buffer = await newWorkbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), `${sheet.name}.xlsx`);
          }

          // Reset state after successful split
          setError('');

        } catch (err) {
          throw err;
        }
      };

      reader.onerror = () => {
        throw reader.error;
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      setError('Error splitting workbook: ' + err.message);
    }
  };

  return (
    <Container className="tool-container">
      <Helmet>
        <title>Excel Sheet Splitter - Split Excel Workbooks by Sheet | Excel Toolbox</title>
        <meta name="description" content="Free tool to split Excel workbooks into separate files by worksheet. Extract individual sheets while preserving formatting. Process files securely on your device." />
        <meta name="keywords" content="split excel sheets, excel sheet splitter, separate excel worksheets, split workbook" />
        <meta property="og:title" content="Excel Sheet Splitter - Split Excel Workbooks by Sheet" />
        <meta property="og:description" content="Split Excel workbooks into separate files by worksheet. Process files securely on your local device." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://exceltoolbox.org/excel-sheet-splitter" />
      </Helmet>

      <Card className="tool-card">
        <Card.Header as="h5" className="tool-header">Excel Sheet Splitter</Card.Header>
        <Card.Body className="tool-body">
          <Card.Text className="mb-4">
            Split Excel worksheets from a single workbook into separate files. Each worksheet will be saved as an individual Excel file.
          </Card.Text>

          <div className="dropzone-container">
            <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
              <input {...getInputProps()} />
              <div className="dropzone-icon">
                <i className="bi bi-cloud-upload"></i>
              </div>
              {file ? (
                <p className="dropzone-text">Selected file: {file.name}</p>
              ) : (
                <p className="dropzone-text">
                  {isDragActive ? 'Drop the file here' : 'Drag and drop an Excel file here, or click to select'}
                </p>
              )}
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <ProcessingProgress
            progress={progress}
            status={status}
            isComplete={isComplete}
            isLoading={isLoading}
            completionMessage="Sheets have been successfully split!"
          />

          <Button 
            variant="success" 
            onClick={splitWorkbook}
            disabled={!file || isLoading}
            className="action-button"
          >
            {isLoading ? 'Processing...' : 'Split Workbook'}
          </Button>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SheetSplitter;
